import { memo, useMemo, useState } from "react"
import "./register"
import { ChartSkeleton } from "./ChartSkeleton"
import { ChartErrorBanner } from "./ChartErrorBanner"
import { ChartRenderer, type ChartSpec, type ChartType, CHART_TYPES } from "./ChartRenderer"

// Pie charts have no axes, so the agent is allowed to omit xLabel/yLabel.
const REQUIRED_FIELDS_BY_TYPE: Record<ChartType, readonly string[]> = {
  bar: ["type", "title", "xLabel", "yLabel", "data"],
  line: ["type", "title", "xLabel", "yLabel", "data"],
  candlestick: ["type", "title", "xLabel", "yLabel", "data"],
  pie: ["type", "title", "data"],
}

interface ChartBlockProps {
  complete: boolean
  jsonStr: string
}

interface ValidationOutcome {
  ok: boolean
  reason?: string
  spec?: ChartSpec
}

function validate(jsonStr: string): ValidationOutcome {
  let parsed: unknown
  try {
    parsed = JSON.parse(jsonStr)
  } catch (err) {
    return { ok: false, reason: `invalid JSON (${(err as Error).message})` }
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ok: false, reason: "spec must be a JSON object" }
  }
  const spec = parsed as Record<string, unknown>
  if (!("type" in spec)) {
    return { ok: false, reason: "missing fields: type" }
  }
  const type = spec.type as ChartType
  if (!CHART_TYPES.includes(type)) {
    return {
      ok: false,
      reason: `unknown type '${spec.type}' (allowed: ${CHART_TYPES.join(", ")})`,
    }
  }
  const missing = REQUIRED_FIELDS_BY_TYPE[type].filter(k => !(k in spec))
  if (missing.length > 0) {
    return { ok: false, reason: `missing fields: ${missing.join(", ")}` }
  }
  if (!Array.isArray(spec.data) || spec.data.length === 0) {
    return { ok: false, reason: "data must be a non-empty array" }
  }
  // Soft cap to protect against canvas OOM on pathological inputs.
  if (spec.data.length > 500) {
    return { ok: false, reason: `data array too large (${spec.data.length} rows, max 500)` }
  }
  return { ok: true, spec: spec as unknown as ChartSpec }
}

function ChartBlockImpl({ complete, jsonStr }: ChartBlockProps) {
  const [dismissed, setDismissed] = useState(false)
  // JSON.parse on a partial fence throws on every streaming token; only
  // validate once the fence has actually closed.
  const outcome = useMemo<ValidationOutcome>(() => {
    if (!complete) return { ok: false, reason: "streaming" }
    return validate(jsonStr)
  }, [complete, jsonStr])

  if (!complete) return <ChartSkeleton />
  if (!outcome.ok) {
    if (dismissed) return null
    return (
      <ChartErrorBanner
        reason={outcome.reason ?? "unknown error"}
        rawJson={jsonStr}
        onDismiss={() => setDismissed(true)}
      />
    )
  }
  return <ChartRenderer spec={outcome.spec as ChartSpec} />
}

export const ChartBlock = memo(ChartBlockImpl)
